import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../services/subscriptionService';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load subscription and usage data
  const loadSubscriptionData = async () => {
    if (!user?.userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [subscriptionData, usageData] = await Promise.all([
        subscriptionService.getCurrentSubscription(user.userId),
        subscriptionService.getUsage(user.userId)
      ]);
      
      setSubscription(subscriptionData);
      setUsage(usageData);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      // Default to free tier on error
      setSubscription({ tier: 'FREE', status: 'active' });
      setUsage({ propertyAnalyses: 0, chatMessages: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Load data when user changes
  useEffect(() => {
    loadSubscriptionData();
  }, [user]);

  // Track usage for property analysis
  const trackPropertyAnalysis = () => {
    if (usage) {
      setUsage(prev => ({
        ...prev,
        propertyAnalyses: prev.propertyAnalyses + 1
      }));
    }
  };

  // Track usage for chat messages
  const trackChatMessage = () => {
    if (usage) {
      setUsage(prev => ({
        ...prev,
        chatMessages: prev.chatMessages + 1
      }));
    }
  };

  // Check if user can perform action
  const canPerformAction = (action) => {
    if (!subscription || !usage) return false;
    return subscriptionService.canPerformAction(subscription, usage, action);
  };

  // Get usage percentage
  const getUsagePercentage = (action) => {
    if (!subscription || !usage) return 0;
    return subscriptionService.getUsagePercentage(subscription, usage, action);
  };

  // Upgrade subscription
  const upgradeSubscription = async (tier) => {
    try {
      const checkoutSession = await subscriptionService.createCheckoutSession(tier, user.userId);
      window.location.href = checkoutSession.url;
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      throw error;
    }
  };

  // Manage subscription
  const manageSubscription = async () => {
    try {
      const portalSession = await subscriptionService.createPortalSession(user.userId);
      window.location.href = portalSession.url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      throw error;
    }
  };

  const value = {
    subscription,
    usage,
    loading,
    canPerformAction,
    getUsagePercentage,
    trackPropertyAnalysis,
    trackChatMessage,
    upgradeSubscription,
    manageSubscription,
    refreshSubscription: loadSubscriptionData
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};